import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiDownloadSimpleLight, PiFileTextLight, PiFolderOpenLight, PiShareNetworkLight } from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import { Well } from '../shared/brand/Field';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { validateFileSecure } from '../../utils/security/fileSecurityValidator';
import {
  EngagementRole,
  MatterDocument,
  SharedDocument,
  addSharedDocuments,
  downloadSharedDocument,
  errorMessage,
  listMatterDocuments,
  listSharedDocuments,
  shareDocument,
  unshareDocument,
} from '../../services/api/marketplaceService';
import { fmtDate } from './marketplaceUi';

const MAX_FILES = 10; // mirrors the backend

const fmtSize = (bytes: number | null): string =>
  bytes == null ? '' : bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Files between a client and the lawyer they hired. The files live in the
 * lawyer's matter; the client sees only what is shared with them and what they
 * added. Client: add files (they go into the matter). Lawyer: share files from
 * the matter, or stop sharing them.
 */
const EngagementDocuments: React.FC<{ leadUuid: string; as: EngagementRole; canAdd: boolean }> = ({
  leadUuid, as, canAdd,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [docs, setDocs] = useState<SharedDocument[] | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  // null: closed; 'loading': opened, files on their way.
  const [picker, setPicker] = useState<MatterDocument[] | 'loading' | null>(null);

  const toast = useCallback((modalType: 'success' | 'error' | 'warning', message: string, subMsg?: string) =>
    dispatch(showFeedbackModal({ showModal: true, modalType, duration: 5000, message, ...(subMsg ? { subMsg } : {}) })),
  [dispatch]);

  const load = useCallback(async () => {
    try {
      setDocs(await listSharedDocuments(leadUuid, as));
    } catch {
      setDocs((d) => d ?? []);
    }
  }, [leadUuid, as]);

  useEffect(() => { load(); }, [load]);

  /** Browser-side screen (magic bytes, type, size); the server re-checks everything. */
  const pickFiles = async (next: File[]) => {
    const fresh = next.filter((f) => !files.includes(f));
    const checked = await Promise.all(fresh.map(async (f) => ({ f, r: await validateFileSecure(f) })));
    const rejected = checked.filter(({ r }) => !r.isValid).map(({ f }) => f.name);
    if (rejected.length) toast('warning', t('Some files were not added'), rejected.join(', '));
    const accepted = checked.filter(({ r }) => r.isValid).map(({ f, r }) => r.processedFile ?? f);
    setFiles([...next.filter((f) => files.includes(f)), ...accepted].slice(0, MAX_FILES));
  };

  const upload = async () => {
    setBusy('upload');
    try {
      const result = await addSharedDocuments(leadUuid, files);
      if (result.skipped.length) {
        toast('warning', t('Some documents were not attached'), result.skipped.map((x) => x.file_name).join(', '));
      }
      if (result.added) toast('success', t('Sent to your lawyer'));
      setFiles([]);
      await load();
    } catch (err) {
      toast('error', errorMessage(err, t('Your documents could not be attached')));
    } finally {
      setBusy(null);
    }
  };

  const download = async (doc: SharedDocument) => {
    setBusy(doc.uuid);
    try {
      await downloadSharedDocument(leadUuid, as, doc);
    } catch (err) {
      toast('error', errorMessage(err, t('Could not download the file')));
    } finally {
      setBusy(null);
    }
  };

  const openPicker = async () => {
    setPicker('loading');
    try {
      setPicker(await listMatterDocuments(leadUuid));
    } catch (err) {
      setPicker(null);
      toast('error', errorMessage(err, t("Could not load the matter's files")));
    }
  };

  const share = async (fileUuid: string) => {
    setBusy(fileUuid);
    try {
      await shareDocument(leadUuid, fileUuid);
      toast('success', t('Shared with your client'));
      setPicker((p) => (Array.isArray(p) ? p.map((f) => (f.file_uuid === fileUuid ? { ...f, shared: true } : f)) : p));
      await load();
    } catch (err) {
      toast('error', errorMessage(err, t('Could not share the file')));
    } finally {
      setBusy(null);
    }
  };

  const unshare = async (doc: SharedDocument) => {
    setBusy(doc.uuid);
    try {
      await unshareDocument(leadUuid, doc.uuid);
      await load();
    } catch (err) {
      toast('error', errorMessage(err, t('Could not stop sharing the file')));
    } finally {
      setBusy(null);
    }
  };

  const fromLabel = (doc: SharedDocument): string =>
    doc.mine ? t('From you') : as === 'client' ? t('From your lawyer') : t('From your client');

  return (
    <GlassCard padding="none">
      <div className="space-y-2 px-4 py-4" data-testid="marketplace-shared-documents">
        {docs === null ? (
          <Skeleton variant="list" rows={2} />
        ) : docs.length === 0 ? (
          <p className="text-sm text-fg-muted">
            {as === 'client'
              ? t('No documents yet. Files you add go to your lawyer; files they share appear here.')
              : t('No documents yet. Files your client adds, and files you share, appear here.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {docs.map((d) => (
              <li key={d.uuid}>
                <Well className="flex items-center gap-3 px-3 py-2">
                  <PiFileTextLight className="h-4 w-4 shrink-0 text-fg-muted" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-fg-body" title={d.name}>{d.name}</div>
                    <div className="text-[11px] text-fg-muted">
                      {fromLabel(d)} · {fmtDate(d.shared_at)}{d.size != null ? ` · ${fmtSize(d.size)}` : ''}
                    </div>
                  </div>
                  {as === 'lawyer' && d.direction === 'to_client' && (
                    <Button variant="outline" size="small" onClick={() => unshare(d)} disabled={busy !== null}>
                      {t('Stop sharing')}
                    </Button>
                  )}
                  <Button variant="outline" size="small" onClick={() => download(d)} isLoading={busy === d.uuid}
                    disabled={busy !== null} icon={<PiDownloadSimpleLight className="h-3.5 w-3.5" />}>
                    {t('Download')}
                  </Button>
                </Well>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canAdd && as === 'client' && (
        <div className="space-y-2 border-t border-hairline-soft px-4 py-3">
          <p className="text-[11px] text-fg-muted">{t('Add documents for your lawyer. They go straight into your case.')}</p>
          <FileUploader files={files} onFilesChange={pickFiles} maxFiles={MAX_FILES} />
          {files.length > 0 && (
            <div className="flex justify-end">
              <Button variant="primary" size="small" onClick={upload} isLoading={busy === 'upload'} disabled={busy !== null}>
                {t('Send to my lawyer')}
              </Button>
            </div>
          )}
        </div>
      )}

      {canAdd && as === 'lawyer' && (
        <div className="space-y-2 border-t border-hairline-soft px-4 py-3">
          {picker === null ? (
            <Button variant="outline" size="small" onClick={openPicker}
              icon={<PiFolderOpenLight className="h-3.5 w-3.5" />}>
              {t('Share a file from the matter')}
            </Button>
          ) : picker === 'loading' ? (
            <Skeleton variant="list" rows={2} />
          ) : (
            <ul className="space-y-1.5" data-testid="marketplace-matter-picker">
              {picker.length === 0 && <li className="text-[11px] text-fg-muted">{t('No files in the matter yet.')}</li>}
              {picker.map((f) => (
                <li key={f.file_uuid} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-fg-body" title={f.name}>
                    {f.name}
                    {f.from_client && <span className="ml-1 text-[11px] text-fg-muted">({t('from your client')})</span>}
                  </span>
                  {f.shared ? (
                    <span className="shrink-0 text-[11px] text-fg-muted">{t('Shared')}</span>
                  ) : (
                    <Button variant="outline" size="small" onClick={() => share(f.file_uuid)}
                      isLoading={busy === f.file_uuid} disabled={busy !== null}
                      icon={<PiShareNetworkLight className="h-3.5 w-3.5" />}>
                      {t('Share')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </GlassCard>
  );
};

export default EngagementDocuments;
